import type { PerpMarket } from "@/hooks/airdrop-farming/types";
import { MarketType } from "@cede/trading-data";
import { assignMarketCategories } from "@/utils/trading";
import Decimal from "decimal.js";

// ── Raw Extended API types ────────────────────────────────────────────────────

export interface ExtendedRawMarket {
  name: string; // "BTC-USD"
  asset_name: string; // "BTC"
  quote_currency: string; // "USD"
  settlement_currency: string; // "USDC"
  status: string; // "active"
  min_order_size: string; // "0.001"
  price_tick_size: string;
  trading_config: {
    max_leverage: number; // e.g. 20
  };
}

export interface ExtendedRawMarketStats {
  market: string; // "BTC-USD"
  mark_price: string;
  best_bid: string;
  best_ask: string;
  volume_24h: string;
  open_interest: string;
  funding_rate: string;
  price_change_rate_24h: string; // Decimal rate — e.g. "0.0894" means +8.94%
}

export interface ExtendedMarketsResponse {
  markets: ExtendedRawMarket[];
}

export interface ExtendedMarketsStatsResponse {
  stats: ExtendedRawMarketStats[];
}

/**
 * Normalizes Extended REST API responses to the generic PerpMarket format.
 *
 * Key mapping notes:
 * - Market format: "BTC-USD" → pair: "BTC/USD:USDC"
 * - `min_order_size` is used to derive szDecimals
 * - `maxLeverage` from `trading_config.max_leverage`
 * - `price_change_rate_24h` is a decimal rate → multiply by 100
 * - Extended is cross-margin only → `onlyIsolated: false`
 */
export function adaptExtendedMarkets(markets: ExtendedRawMarket[], stats: ExtendedRawMarketStats[]): PerpMarket[] {
  const statsByMarket = new Map<string, ExtendedRawMarketStats>();
  for (const s of stats) {
    statsByMarket.set(s.market, s);
  }

  return markets
    .filter((m) => m.status === "active")
    .map((market): PerpMarket => {
      const stat = statsByMarket.get(market.name);

      const markPrice = stat?.mark_price;
      const currentPriceDec = markPrice ? new Decimal(markPrice) : new Decimal(0);

      // Decimal rate → percentage
      const rawRateDec = stat ? new Decimal(stat.price_change_rate_24h) : null;
      const rawRate = rawRateDec?.toNumber() ?? NaN;
      const priceChange24h = isNaN(rawRate) ? 0 : rawRateDec!.mul(100).toNumber();

      const prevDayPrice =
        stat && currentPriceDec.gt(0) && rawRateDec && !rawRateDec.eq(-1)
          ? currentPriceDec.div(rawRateDec.plus(1)).toString()
          : undefined;

      const bidDec = stat ? new Decimal(stat.best_bid) : new Decimal(0);
      const askDec = stat ? new Decimal(stat.best_ask) : new Decimal(0);
      const midPrice = bidDec.gt(0) && askDec.gt(0) ? bidDec.plus(askDec).div(2).toString() : markPrice;

      // szDecimals from min_order_size: "0.001" → 3, "1" → 0
      const decimalPart = market.min_order_size.split(".")[1];
      const szDecimals = decimalPart ? decimalPart.length : 0;

      const maxLeverage = market.trading_config?.max_leverage;

      // "BTC-USD" → "BTC/USD:USDC"
      const [base, quote] = market.name.split("-");
      const pair = `${base}/${quote}:${market.settlement_currency}`;

      return {
        type: MarketType.PERP,
        symbol: market.name,
        pair,
        baseAsset: market.asset_name,
        quoteAsset: market.settlement_currency,
        markPrice,
        midPrice,
        volume24h: stat?.volume_24h,
        priceChange24h,
        prevDayPrice,
        funding: stat?.funding_rate,
        openInterest: stat?.open_interest,
        maxLeverage,
        szDecimals,
        onlyIsolated: false,
        isDelisted: false,
        isGrowthMode: false,
        searchString: `${market.asset_name.toLowerCase()}perp`,
        categories: assignMarketCategories(market.asset_name, MarketType.PERP),
      };
    });
}
